class BundleBuilder {
  constructor() {
    const bundleElement = document.querySelector(".bundle-builder");

    // Read from data attributes with fallbacks
    this.minProducts = parseInt(bundleElement?.dataset.minProducts) || 2;
    this.maxProducts = parseInt(bundleElement?.dataset.maxProducts) || 9;

    // Initialize array with dynamic size
    this.selectedProducts = new Array(this.maxProducts).fill(null);
    this.currentProduct = null;
    this.currentOptions = null;
    this.currentVariants = null;
    this.visiblePlaceholders = 3;
    this.modal = document.getElementById("productModal");
    this.addToCartButton = document.getElementById("addBundleToCart");

    // Initialize after ensuring ReCharge is loaded
    this.initializeWhenReady();
  }

  async waitForRecharge(timeout = 10000) {
    return new Promise((resolve) => {
      // If ReCharge is already available, resolve immediately
      if (window.recharge) {
        console.log("ReCharge API found immediately");
        resolve(window.recharge);
        return;
      }

      let timeElapsed = 0;
      const interval = 100; // Check every 100ms

      const checkRecharge = setInterval(() => {
        timeElapsed += interval;

        if (window.recharge) {
          console.log("ReCharge API found after waiting");
          clearInterval(checkRecharge);
          resolve(window.recharge);
        } else if (timeElapsed >= timeout) {
          console.warn("ReCharge API not found after timeout");
          clearInterval(checkRecharge);
          resolve(null);
        }
      }, interval);
    });
  }

  async initializeWhenReady() {
    try {
      // Wait for ReCharge to be available
      const rechargeApi = await this.waitForRecharge(10000); // Increase timeout to 10 seconds
      if (!rechargeApi) {
        console.error(
          "ReCharge API failed to load. Bundle functionality may be limited."
        );
      } else {
        console.log("ReCharge API loaded successfully");
      }
    } catch (error) {
      console.error("Error initializing ReCharge:", error);
    }

    // Initialize bundle builder regardless of ReCharge status
    this.init();
  }

  init() {
    // Initialize bundle builder without recreating placeholders
    this.bindProductCards();
    this.bindAddToCartButton();
    this.initializeModal();
    this.initMaxAlert();
    this.bindFeaturedProducts();
  }

  initializeModal() {
    const closeBtn = this.modal.querySelector(".product-modal__close");
    const addToBundle = this.modal.querySelector(
      ".product-modal__add-to-bundle"
    );

    closeBtn.addEventListener("click", () => this.closeModal());
    addToBundle.addEventListener("click", () =>
      this.addCurrentProductToBundle()
    );
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.closeModal();
    });
  }

  openProductModal(card) {
    this.setupModalContent(card);
    requestAnimationFrame(() => {
      this.modal.classList.add("active");
      document.body.style.overflow = "hidden";
    });
  }

  closeModal() {
    this.modal.classList.remove("active");
    setTimeout(() => {
      document.body.style.overflow = "";
      this.currentProduct = null;
    }, 300);
  }

  setupModalContent(card) {
    const { productId, hasOptions, productPrice, description } = card.dataset;
    const title = card.querySelector(".product-card__title").textContent;
    const images = card.dataset.images.split("|").filter(Boolean);
    const only10oz =
      document
        .querySelector(".bundle-builder")
        .getAttribute("data-only-10oz") === "true";

    // Update modal content
    this.modal.querySelector(".product-modal__title").textContent = title;

    // Set initial price display
    if (hasOptions === "true") {
      const variants = JSON.parse(card.dataset.variants);
      if (variants && variants.length > 0) {
        // Filter for 10oz variants if only10oz is true
        const filteredVariants = only10oz
          ? variants.filter((v) =>
              v.options.some((option) => option === "10oz")
            )
          : variants;

        if (filteredVariants.length > 0) {
          // Find the lowest price among filtered variants
          const lowestPrice = Math.min(...filteredVariants.map((v) => v.price));
          this.modal.querySelector(".product-modal__price").textContent =
            this.formatMoney(lowestPrice);
        } else {
          this.modal.querySelector(".product-modal__price").textContent =
            this.formatMoney(productPrice);
        }
      }
    } else {
      this.modal.querySelector(".product-modal__price").textContent =
        this.formatMoney(productPrice);
    }

    this.modal.querySelector(".product-modal__description").textContent =
      description || "No description available.";

    // Setup image carousel
    this.setupImageCarousel(images, title);

    const optionsContainer = this.modal.querySelector(
      ".product-modal__options"
    );
    const addToBundle = this.modal.querySelector(
      ".product-modal__add-to-bundle"
    );

    // Check if product is already in bundle
    const isInBundle = this.selectedProducts.some(
      (product) => product && product.id === productId
    );

    // Store current product data
    this.currentProduct = {
      id: productId,
      price: parseFloat(productPrice),
      card,
      hasOptions: hasOptions === "true",
      selectedVariant: null,
      selectedOptions: {},
    };

    if (this.currentProduct.hasOptions) {
      this.setupProductOptions(card, optionsContainer, addToBundle);
    } else {
      optionsContainer.style.display = "none";
      addToBundle.disabled = isInBundle;
    }

    // If product is in bundle, disable the add button and update text
    if (isInBundle) {
      addToBundle.disabled = true;
      addToBundle.textContent = "In Bundle";
    } else {
      addToBundle.textContent = "Add to Bundle";
    }
  }

  setupProductOptions(card, optionsContainer, addToBundle) {
    this.currentOptions = JSON.parse(card.dataset.options);
    this.currentVariants = JSON.parse(card.dataset.variants);

    // Get only_10oz setting
    const only10oz =
      document
        .querySelector(".bundle-builder")
        .getAttribute("data-only-10oz") === "true";

    // Check if product has only 10oz variant
    const hasOnly10oz = this.currentVariants.every((variant) =>
      variant.options.some((option) => option === "10oz")
    );

    // Filter and modify options based on settings
    const filteredOptions = this.currentOptions.map((option) => {
      if (option.name === "Size") {
        if (only10oz || hasOnly10oz) {
          // Only show 10oz as the single option
          return {
            ...option,
            values: option.values.filter((value) => value === "10oz"),
            default: "10oz",
          };
        } else {
          // If multiple sizes available, keep all options but default to 10oz
          return {
            ...option,
            default: "10oz",
          };
        }
      }
      return option;
    });

    const optionSelectors = this.modal.querySelector(
      ".product-modal__option-selectors"
    );
    optionSelectors.innerHTML = filteredOptions
      .map((option, index) => this.createOptionHTML(option, index))
      .join("");

    // Pre-select and disable the button if only one value (e.g., 10oz)
    filteredOptions.forEach((option, optionIndex) => {
      if (option.values.length === 1) {
        const btn = optionSelectors.querySelector(
          `.product-modal__option-button[data-value="${option.values[0]}"]`
        );
        if (btn) {
          btn.classList.add("selected");
          btn.disabled = true;
          this.currentProduct.selectedOptions[option.name] = option.values[0];
        }
      }
    });

    // Check if product is already in bundle
    const isInBundle = this.selectedProducts.some(
      (product) => product && product.id === this.currentProduct.id
    );

    // If product is in bundle, disable all option buttons
    if (isInBundle) {
      optionSelectors
        .querySelectorAll(".product-modal__option-button")
        .forEach((button) => {
          button.disabled = true;
        });
    } else {
      this.bindOptionButtons(optionSelectors);
    }

    optionsContainer.style.display = "block";
    // Enable add to bundle if only one option is available and not in bundle
    if (
      filteredOptions.every((option) => option.values.length === 1) &&
      !isInBundle
    ) {
      // Set the default variant to 10oz
      const tenOzVariant = this.currentVariants.find((variant) =>
        variant.options.some((option) => option === "10oz")
      );
      if (tenOzVariant) {
        this.currentProduct.selectedVariant = tenOzVariant;
        addToBundle.disabled = false;
      }
    } else {
      addToBundle.disabled = true;
    }

    // Update add button text if in bundle
    if (isInBundle) {
      addToBundle.textContent = "In Bundle";
    }
  }

  createOptionHTML(option, index) {
    return `
      <div class="product-modal__option">
        <label class="product-modal__option-label">${option.name}</label>
        <div class="product-modal__option-buttons" data-option-name="${
          option.name
        }">
          ${option.values
            .map(
              (value) => `
            <button type="button" 
              class="product-modal__option-button" 
              data-value="${value}"
              data-option-index="${index}">${value}</button>
          `
            )
            .join("")}
        </div>
      </div>
    `;
  }

  bindOptionButtons(optionSelectors) {
    optionSelectors
      .querySelectorAll(".product-modal__option-button")
      .forEach((button) => {
        button.addEventListener("click", () => {
          const { optionIndex, value } = button.dataset;
          const optionName = this.currentOptions[optionIndex].name;

          // Update selected state
          const buttons = button
            .closest(".product-modal__option-buttons")
            .querySelectorAll(".product-modal__option-button");
          buttons.forEach((b) => b.classList.remove("selected"));
          button.classList.add("selected");

          // Update selected options and check variant
          this.currentProduct.selectedOptions[optionName] = value;
          this.updateVariantSelection();
        });
      });
  }

  updateVariantSelection() {
    if (!this.currentProduct || !this.currentVariants) return;

    const selectedOptions = this.currentProduct.selectedOptions;
    const allOptionsSelected = this.currentOptions.every(
      (option) => selectedOptions[option.name] !== undefined
    );

    const matchingVariant =
      allOptionsSelected &&
      this.currentVariants.find((variant) =>
        this.currentOptions.every(
          (option, index) =>
            variant.options[index] === selectedOptions[option.name]
        )
      );

    const addToBundle = this.modal.querySelector(
      ".product-modal__add-to-bundle"
    );
    const priceElement = this.modal.querySelector(".product-modal__price");

    // Check if product is already in bundle
    const isInBundle = this.selectedProducts.some(
      (product) => product && product.id === this.currentProduct.id
    );

    if (matchingVariant?.available && !isInBundle) {
      addToBundle.disabled = false;
      this.currentProduct.selectedVariant = matchingVariant;
      this.currentProduct.price = matchingVariant.price;

      // Update price display
      if (priceElement) {
        priceElement.textContent = this.formatMoney(matchingVariant.price);
      }

      this.updateAvailableOptions();
    } else {
      addToBundle.disabled = true;
      this.currentProduct.selectedVariant = null;
      this.currentProduct.price = null;

      // Reset price display if no valid variant
      if (priceElement) {
        const basePrice = parseFloat(
          this.currentProduct.card.dataset.productPrice
        );
        priceElement.textContent = this.formatMoney(basePrice);
      }
    }
  }

  updateAvailableOptions() {
    // Check if product is in bundle
    const isInBundle = this.selectedProducts.some(
      (product) => product && product.id === this.currentProduct.id
    );
    if (isInBundle) return; // Don't update availability if product is in bundle

    const selectedOptions = this.currentProduct.selectedOptions;

    this.currentOptions.forEach((option, optionIndex) => {
      const optionButtons = document.querySelectorAll(
        `.product-modal__option-buttons[data-option-name="${option.name}"] .product-modal__option-button`
      );

      optionButtons.forEach((button) => {
        const value = button.dataset.value;
        const wouldBeOptions = { ...selectedOptions, [option.name]: value };

        const hasAvailableVariant = this.currentVariants.some(
          (variant) =>
            this.currentOptions.every(
              (opt, index) =>
                variant.options[index] ===
                (wouldBeOptions[opt.name] || selectedOptions[opt.name])
            ) && variant.available
        );

        button.classList.toggle("unavailable", !hasAvailableVariant);
        button.disabled = !hasAvailableVariant || isInBundle;
      });
    });
  }

  addCurrentProductToBundle() {
    if (!this.currentProduct) return;

    // Check if bundle is full
    const filledPositions = this.selectedProducts.filter(
      (product) => product !== null
    ).length;
    if (filledPositions >= this.maxProducts) {
      this.closeModal();
      this.showMaxAlert();
      return;
    }

    const nextPosition = this.findNextEmptyPosition();
    if (nextPosition !== -1) {
      // Get variant/option information
      let variantId, optionSummary;
      if (this.currentProduct.hasOptions) {
        if (!this.currentProduct.selectedVariant) return;
        variantId = this.currentProduct.selectedVariant.id.toString();
        optionSummary = this.currentProduct.selectedVariant.title;
      } else {
        const defaultVariantId = this.currentProduct.card.querySelector(
          ".product-card__add-to-bundle"
        ).dataset.variantId;
        variantId = defaultVariantId;
        optionSummary = "";
      }

      // Add product to next available position
      this.selectedProducts[nextPosition] = {
        id: this.currentProduct.id,
        variantId: variantId,
        optionSummary: optionSummary,
        price: this.currentProduct.price,
      };

      // GTM Tracking: Track bundle product added
      if (window.bundleBuilderGTM) {
        const card = this.currentProduct.card;
        const bundleSize = this.selectedProducts.filter(
          (p) => p !== null
        ).length;
        window.bundleBuilderGTM.trackBundleProductAdded({
          id: card.dataset.gtmProductId || this.currentProduct.id,
          name: card.dataset.gtmProductTitle || this.currentProduct.title,
          price: parseFloat(
            card.dataset.gtmProductPrice || this.currentProduct.price
          ),
          position: card.dataset.gtmProductPosition || nextPosition + 1,
          bundleSize: bundleSize,
        });
      }

      // Update UI
      this.updateBundlePreview();
      this.updateAddToCartButton();
      this.updateBundleTotal();
      this.updateProductSelectionStates();
    }

    // Close modal
    this.closeModal();
  }

  bindProductCards() {
    const productCards = document.querySelectorAll(".product-card");
    const only10oz =
      document
        .querySelector(".bundle-builder")
        .getAttribute("data-only-10oz") === "true";

    productCards.forEach((card) => {
      const addToBundleBtn = card.querySelector(".product-card__add-to-bundle");
      const priceElement = card.querySelector(".product-card__price");

      // Check if product has options
      const hasOptions = card.dataset.hasOptions === "true";
      if (hasOptions) {
        const variants = JSON.parse(card.dataset.variants);
        if (variants && variants.length > 0) {
          // Find the 10oz variant
          const tenOzVariant = variants.find((variant) =>
            variant.options.some((option) => option === "10oz")
          );

          // Update price display to show 10oz price only
          if (tenOzVariant) {
            priceElement.textContent = this.formatMoney(tenOzVariant.price);
          } else {
            // If no 10oz variant, show the first available variant price
            priceElement.textContent = this.formatMoney(variants[0].price);
          }
        }
      }

      // Handle the "Add to Bundle" or "Select Options" button click
      addToBundleBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productId = card.dataset.productId;
        const isInBundle = this.selectedProducts.some(
          (product) => product && product.id === productId
        );
        const buttonText = addToBundleBtn.textContent.trim();

        if (isInBundle) {
          const position = this.findProductPosition(productId);
          if (position !== -1) {
            this.removeProduct(position);
          }
        } else if (buttonText === "Select Options") {
          this.openProductModal(card);
        } else {
          this.handleProductSelection(card);
        }
      });

      // Card click opens modal only for viewing details (not button)
      card.addEventListener("click", (e) => {
        if (!e.target.closest(".product-card__add-to-bundle")) {
          this.openProductModal(card);
        }
      });
    });
  }

  findNextEmptyPosition() {
    return this.selectedProducts.findIndex((product) => product === null);
  }

  findProductPosition(productId) {
    return this.selectedProducts.findIndex(
      (product) => product && product.id === productId
    );
  }

  handleProductSelection(card) {
    const productId = card.dataset.productId;
    const variantId = card.querySelector(".product-card__add-to-bundle").dataset
      .variantId;
    const productPrice = parseFloat(card.dataset.productPrice);

    // Check if product is already selected
    const existingPosition = this.findProductPosition(productId);
    if (existingPosition !== -1) {
      // If already selected, remove it
      this.removeProduct(existingPosition);
    } else {
      // Check if bundle is full
      const filledPositions = this.selectedProducts.filter(
        (product) => product !== null
      ).length;
      if (filledPositions >= this.maxProducts) {
        this.showMaxAlert();
        return;
      }

      // Find next empty position
      const nextPosition = this.findNextEmptyPosition();
      if (nextPosition !== -1) {
        // Add product to next available position
        this.selectedProducts[nextPosition] = {
          id: productId,
          variantId: variantId,
          price: productPrice,
          optionSummary: "",
        };

        // GTM Tracking: Track bundle product added
        if (window.bundleBuilderGTM) {
          const bundleSize = this.selectedProducts.filter(
            (p) => p !== null
          ).length;
          window.bundleBuilderGTM.trackBundleProductAdded({
            id: card.dataset.gtmProductId || productId,
            name: card.dataset.gtmProductTitle || "Product",
            price: parseFloat(card.dataset.gtmProductPrice || productPrice),
            position: card.dataset.gtmProductPosition || nextPosition + 1,
            bundleSize: bundleSize,
          });
        }

        // Update UI
        this.updateBundlePreview();
        this.updateAddToCartButton();
        this.updateBundleTotal();
        this.updateProductSelectionStates();
      }
    }
  }

  removeProduct(index) {
    if (this.selectedProducts[index]) {
      const removedProduct = this.selectedProducts[index];

      // GTM Tracking: Track bundle product removed
      if (window.bundleBuilderGTM) {
        // Try to find the card to get GTM data
        const card = document.querySelector(
          `[data-product-id="${removedProduct.id}"]`
        );
        const bundleSize =
          this.selectedProducts.filter((p) => p !== null).length - 1; // Subtract 1 for the one being removed
        window.bundleBuilderGTM.trackBundleProductRemoved({
          id: card?.dataset.gtmProductId || removedProduct.id,
          name: card?.dataset.gtmProductTitle || "Product",
          bundleSize: bundleSize,
        });
      }

      // Remove the product
      this.selectedProducts[index] = null;

      // Shift remaining products to fill the gap
      this.compactSelectedProducts();

      // Update all UI elements
      this.updateBundlePreview();
      this.updateAddToCartButton();
      this.updateBundleTotal();
      this.updateProductSelectionStates();
    }
  }

  updateProductSelectionStates() {
    // First, remove selected state from all products and reset button text/state
    document.querySelectorAll(".product-card").forEach((card) => {
      card.classList.remove("selected");
      const addButton = card.querySelector(".product-card__add-to-bundle");
      const hasOptions = card.dataset.hasOptions === "true";
      const only10oz =
        document
          .querySelector(".bundle-builder")
          .getAttribute("data-only-10oz") === "true";
      const options = card.dataset.options
        ? JSON.parse(card.dataset.options)
        : [];
      const isSingleOption =
        options.length === 1 &&
        options[0].values &&
        options[0].values.length === 1;
      if (only10oz) {
        addButton.textContent = "Add to Bundle";
      } else if (only10oz || isSingleOption) {
        addButton.textContent = "Add to Bundle";
      } else {
        addButton.textContent = hasOptions ? "Select Options" : "Add to Bundle";
      }
      addButton.disabled = false;
    });

    // Then, add selected state to all products in the bundle and update their buttons
    this.selectedProducts.forEach((product) => {
      if (product) {
        document
          .querySelectorAll(`.product-card[data-product-id="${product.id}"]`)
          .forEach((card) => {
            card.classList.add("selected");
            const addButton = card.querySelector(
              ".product-card__add-to-bundle"
            );
            addButton.textContent = "In Bundle";
            addButton.disabled = true;
          });
      }
    });
  }

  compactSelectedProducts() {
    // Remove null values and keep selected products
    const selected = this.selectedProducts.filter(
      (product) => product !== null
    );
    // Fill array back to 9 items with null values at the end
    this.selectedProducts = [
      ...selected,
      ...new Array(9 - selected.length).fill(null),
    ];
  }

  updateBundlePreview() {
    const previewContainer = document.querySelector(".bundle-preview__items");
    const filledPositions = this.selectedProducts.filter(
      (product) => product !== null
    ).length;
    const totalPlaceholders = Math.min(
      Math.max(3, filledPositions + 1),
      this.maxProducts
    );

    // Clear current placeholders
    previewContainer.innerHTML = "";

    for (let i = 0; i < totalPlaceholders; i++) {
      const product = this.selectedProducts[i];
      const placeholder = document.createElement("div");
      placeholder.className = "bundle-placeholder" + (product ? " filled" : "");
      placeholder.dataset.placeholder = (i + 1).toString();

      if (product) {
        const selectedCard = document.querySelector(
          `.product-card[data-product-id="${product.id}"]`
        );
        if (selectedCard) {
          placeholder.innerHTML = `
            <div class="bundle-placeholder__image-container">
              <img class="product-card__image" 
                   src="${
                     selectedCard.querySelector(".product-card__image").src
                   }"
                   alt="${
                     selectedCard.querySelector(".product-card__image").alt
                   }"
                   width="100"
                   height="100">
              <button type="button" class="bundle-remove-btn">
                <svg viewBox="0 0 24 24">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="bundle-placeholder__info">
              <h3 class="bundle-placeholder__title">${
                selectedCard.querySelector(".product-card__title").textContent
              }</h3>
              ${
                product.optionSummary
                  ? `<div class="bundle-placeholder__variant">${product.optionSummary}</div>`
                  : ""
              }
            </div>
          `;
          // Add click handler for remove button
          const removeButton = placeholder.querySelector(".bundle-remove-btn");
          removeButton.addEventListener("click", (e) => {
            e.stopPropagation();
            this.removeProduct(i);
          });
        }
      } else {
        // Empty placeholder
        let emptyText = "Add product";
        let lockIcon = "";
        if (i === 0) {
          emptyText = "Select product";
        } else if (i === 1) {
          emptyText = "10% off";
          lockIcon = `<span class="lock-icon"><svg width="85" height="103" viewBox="0 0 85 103" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M65.0643 9.22798C66.0451 10.1411 66.9872 11.054 67.8198 12.1057C67.9045 12.2106 67.9892 12.3155 68.0764 12.4236C73.6309 19.3434 75.252 27.1014 75.2205 35.7799C75.2201 36.0907 75.2198 36.4015 75.2196 36.7123C75.2189 37.5168 75.2169 38.3213 75.2147 39.1259C75.2126 39.9522 75.2117 40.7786 75.2107 41.605C75.2086 43.2164 75.2052 44.8279 75.201 46.4394C75.5258 46.4355 75.5258 46.4355 75.8571 46.4315C76.1455 46.4294 76.4338 46.4274 76.7221 46.4254C76.9354 46.4226 76.9354 46.4226 77.1531 46.4197C79.4154 46.4081 80.9269 47.0576 82.5635 48.579C84.6921 50.7684 84.4918 53.4523 84.4777 56.2966C84.4785 56.732 84.4796 57.1674 84.481 57.6028C84.4838 58.7804 84.4816 59.958 84.4783 61.1356C84.4755 62.3701 84.4768 63.6046 84.4775 64.8391C84.4781 67.0441 84.4748 69.249 84.4698 71.4539C84.4654 73.4504 84.4647 75.4468 84.4666 77.4433C84.4692 80.0143 84.4686 82.5852 84.4655 85.1562C84.4644 86.1363 84.4644 87.1163 84.4653 88.0964C84.4663 89.2507 84.4642 90.405 84.4599 91.5593C84.4588 91.9823 84.4587 92.4052 84.4597 92.8282C84.4609 93.4067 84.4584 93.9851 84.455 94.5637C84.4568 94.8131 84.4568 94.8131 84.4586 95.0676C84.4364 97.1402 83.6579 98.8435 82.2643 100.373C80.6734 101.858 78.6476 102.231 76.5258 102.232C76.1546 102.233 76.1546 102.233 75.776 102.234C75.5041 102.234 75.2321 102.234 74.9602 102.233C74.6693 102.234 74.3783 102.234 74.0874 102.235C73.2896 102.236 72.4918 102.236 71.694 102.236C70.8329 102.236 69.9718 102.237 69.1107 102.238C67.4243 102.239 65.7378 102.24 64.0514 102.24C62.6803 102.24 61.3092 102.24 59.9381 102.241C56.0495 102.243 52.161 102.244 48.2724 102.244C47.958 102.244 47.958 102.244 47.6373 102.244C47.3225 102.244 47.3225 102.244 47.0014 102.244C43.6012 102.244 40.201 102.245 36.8008 102.248C33.3081 102.251 29.8155 102.252 26.3229 102.252C24.3625 102.252 22.4022 102.253 20.4419 102.255C18.7728 102.257 17.1037 102.257 15.4345 102.256C14.5834 102.255 13.7322 102.255 12.881 102.257C12.1008 102.258 11.3207 102.258 10.5405 102.256C10.2592 102.256 9.97799 102.256 9.69674 102.258C7.01139 102.267 4.59531 102.161 2.57083 100.223C1.00346 98.4938 0.528872 96.8786 0.537356 94.5875C0.536452 94.4062 0.535547 94.2248 0.534616 94.038C0.532102 93.4337 0.532417 92.8294 0.532747 92.225C0.531553 91.7904 0.530195 91.3557 0.528686 90.921C0.525167 89.7425 0.524229 88.5641 0.523974 87.3856C0.52369 86.6487 0.522833 85.9119 0.521776 85.175C0.518277 82.7354 0.516424 80.2958 0.516723 77.8562C0.516739 77.7249 0.516756 77.5936 0.516772 77.4583C0.516788 77.3268 0.516804 77.1953 0.516821 77.0599C0.516983 74.9298 0.513168 72.7997 0.507543 70.6697C0.501802 68.4788 0.499126 66.2879 0.499397 64.097C0.499498 62.8685 0.498373 61.64 0.494143 60.4115C0.490271 59.2557 0.490253 58.1 0.49315 56.9442C0.493567 56.5211 0.492634 56.098 0.490216 55.6749C0.474569 52.7458 0.547543 50.469 2.69941 48.2581C4.35567 46.7905 6.06031 46.4137 8.22676 46.4308C8.375 46.4315 8.52324 46.4321 8.67598 46.4327C9.03978 46.4343 9.40358 46.4367 9.76737 46.4394C9.76693 46.2708 9.76648 46.1022 9.76603 45.9285C9.76199 44.3233 9.75897 42.7181 9.75703 41.1129C9.756 40.288 9.75461 39.4632 9.75232 38.6383C9.75012 37.8392 9.74895 37.0401 9.74844 36.241C9.74808 35.9393 9.74737 35.6376 9.74628 35.3359C9.74061 33.7073 9.76631 32.0847 9.85353 30.4581C9.86097 30.3158 9.8684 30.1734 9.87607 30.0267C10.3797 21.5161 14.637 13.8766 20.914 8.19846C21.1852 7.97554 21.4601 7.75703 21.7369 7.54106C21.8421 7.45708 21.9473 7.37311 22.0557 7.28659C34.9669 -2.97094 53.1153 -1.72638 65.0643 9.22798ZM28.522 20.6976C22.2003 28.2129 24.1308 36.4833 24.1308 46.4394C36.244 46.4394 48.3572 46.4394 60.8375 46.4394C61.6292 36.6816 61.6292 36.6816 59.5907 25.7498C59.5298 25.6179 59.4689 25.4859 59.4062 25.35C57.4847 21.2856 54.3632 17.9927 50.2644 16.0749C50.0764 15.9856 49.8884 15.8963 49.6947 15.8043C42.4222 12.5644 33.726 14.8157 28.522 20.6976Z\" fill=\"#666\"/></svg>`;
        } else if (i === 2) {
          emptyText = "15% off";
          lockIcon = `<span class="lock-icon"><svg width="85" height="103" viewBox="0 0 85 103" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M65.0643 9.22798C66.0451 10.1411 66.9872 11.054 67.8198 12.1057C67.9045 12.2106 67.9892 12.3155 68.0764 12.4236C73.6309 19.3434 75.252 27.1014 75.2205 35.7799C75.2201 36.0907 75.2198 36.4015 75.2196 36.7123C75.2189 37.5168 75.2169 38.3213 75.2147 39.1259C75.2126 39.9522 75.2117 40.7786 75.2107 41.605C75.2086 43.2164 75.2052 44.8279 75.201 46.4394C75.5258 46.4355 75.5258 46.4355 75.8571 46.4315C76.1455 46.4294 76.4338 46.4274 76.7221 46.4254C76.9354 46.4226 76.9354 46.4226 77.1531 46.4197C79.4154 46.4081 80.9269 47.0576 82.5635 48.579C84.6921 50.7684 84.4918 53.4523 84.4777 56.2966C84.4785 56.732 84.4796 57.1674 84.481 57.6028C84.4838 58.7804 84.4816 59.958 84.4783 61.1356C84.4755 62.3701 84.4768 63.6046 84.4775 64.8391C84.4781 67.0441 84.4748 69.249 84.4698 71.4539C84.4654 73.4504 84.4647 75.4468 84.4666 77.4433C84.4692 80.0143 84.4686 82.5852 84.4655 85.1562C84.4644 86.1363 84.4644 87.1163 84.4653 88.0964C84.4663 89.2507 84.4642 90.405 84.4599 91.5593C84.4588 91.9823 84.4587 92.4052 84.4597 92.8282C84.4609 93.4067 84.4584 93.9851 84.455 94.5637C84.4568 94.8131 84.4568 94.8131 84.4586 95.0676C84.4364 97.1402 83.6579 98.8435 82.2643 100.373C80.6734 101.858 78.6476 102.231 76.5258 102.232C76.1546 102.233 76.1546 102.233 75.776 102.234C75.5041 102.234 75.2321 102.234 74.9602 102.233C74.6693 102.234 74.3783 102.234 74.0874 102.235C73.2896 102.236 72.4918 102.236 71.694 102.236C70.8329 102.236 69.9718 102.237 69.1107 102.238C67.4243 102.239 65.7378 102.24 64.0514 102.24C62.6803 102.24 61.3092 102.24 59.9381 102.241C56.0495 102.243 52.161 102.244 48.2724 102.244C47.958 102.244 47.958 102.244 47.6373 102.244C47.3225 102.244 47.3225 102.244 47.0014 102.244C43.6012 102.244 40.201 102.245 36.8008 102.248C33.3081 102.251 29.8155 102.252 26.3229 102.252C24.3625 102.252 22.4022 102.253 20.4419 102.255C18.7728 102.257 17.1037 102.257 15.4345 102.256C14.5834 102.255 13.7322 102.255 12.881 102.257C12.1008 102.258 11.3207 102.258 10.5405 102.256C10.2592 102.256 9.97799 102.256 9.69674 102.258C7.01139 102.267 4.59531 102.161 2.57083 100.223C1.00346 98.4938 0.528872 96.8786 0.537356 94.5875C0.536452 94.4062 0.535547 94.2248 0.534616 94.038C0.532102 93.4337 0.532417 92.8294 0.532747 92.225C0.531553 91.7904 0.530195 91.3557 0.528686 90.921C0.525167 89.7425 0.524229 88.5641 0.523974 87.3856C0.52369 86.6487 0.522833 85.9119 0.521776 85.175C0.518277 82.7354 0.516424 80.2958 0.516723 77.8562C0.516739 77.7249 0.516756 77.5936 0.516772 77.4583C0.516788 77.3268 0.516804 77.1953 0.516821 77.0599C0.516983 74.9298 0.513168 72.7997 0.507543 70.6697C0.501802 68.4788 0.499126 66.2879 0.499397 64.097C0.499498 62.8685 0.498373 61.64 0.494143 60.4115C0.490271 59.2557 0.490253 58.1 0.49315 56.9442C0.493567 56.5211 0.492634 56.098 0.490216 55.6749C0.474569 52.7458 0.547543 50.469 2.69941 48.2581C4.35567 46.7905 6.06031 46.4137 8.22676 46.4308C8.375 46.4315 8.52324 46.4321 8.67598 46.4327C9.03978 46.4343 9.40358 46.4367 9.76737 46.4394C9.76693 46.2708 9.76648 46.1022 9.76603 45.9285C9.76199 44.3233 9.75897 42.7181 9.75703 41.1129C9.756 40.288 9.75461 39.4632 9.75232 38.6383C9.75012 37.8392 9.74895 37.0401 9.74844 36.241C9.74808 35.9393 9.74737 35.6376 9.74628 35.3359C9.74061 33.7073 9.76631 32.0847 9.85353 30.4581C9.86097 30.3158 9.8684 30.1734 9.87607 30.0267C10.3797 21.5161 14.637 13.8766 20.914 8.19846C21.1852 7.97554 21.4601 7.75703 21.7369 7.54106C21.8421 7.45708 21.9473 7.37311 22.0557 7.28659C34.9669 -2.97094 53.1153 -1.72638 65.0643 9.22798ZM28.522 20.6976C22.2003 28.2129 24.1308 36.4833 24.1308 46.4394C36.244 46.4394 48.3572 46.4394 60.8375 46.4394C61.6292 36.6816 61.6292 36.6816 59.5907 25.7498C59.5298 25.6179 59.4689 25.4859 59.4062 25.35C57.4847 21.2856 54.3632 17.9927 50.2644 16.0749C50.0764 15.9856 49.8884 15.8963 49.6947 15.8043C42.4222 12.5644 33.726 14.8157 28.522 20.6976Z\" fill=\"#666\"/></svg>`;
        }
        placeholder.innerHTML = `
          <div class="bundle-placeholder__image-container">
            <span class="empty-text">${emptyText}</span>
            ${lockIcon}
          </div>
        `;
      }
      previewContainer.appendChild(placeholder);
    }
  }

  updateBundleTotal() {
    const selectedProducts = this.selectedProducts.filter(
      (product) => product !== null
    );
    const productCount = selectedProducts.length;

    // Calculate total before discount
    const subtotal = selectedProducts.reduce((sum, product) => {
      return sum + (product ? product.price : 0);
    }, 0);

    // Apply discount based on number of products
    let discountPercentage = 0;
    if (productCount >= 3) {
      discountPercentage = 0.15; // 15% off for 3+ products
    } else if (productCount >= 2) {
      discountPercentage = 0.1; // 10% off for 2 products
    }

    const discount = Math.round(subtotal * discountPercentage);
    const total = subtotal - discount;

    // Update the display
    const bundleTotalElement = document.getElementById("bundleTotal");
    bundleTotalElement.innerHTML = `
      ${
        productCount >= 2
          ? `
        <span class="bundle-total__original">${this.formatMoney(
          subtotal
        )}</span>
      `
          : ""
      }
      <span class="bundle-total__final">${this.formatMoney(total)}</span>
    `;

    // Update savings info in header
    const savingsElement = document.querySelector(".bundle-preview__savings");
    if (productCount === 0) {
      savingsElement.textContent = "";
    } else if (productCount === 1) {
      savingsElement.textContent = "Add more to unlock 10% off";
    } else if (productCount === 2) {
      savingsElement.textContent = "Saving 10%";
    } else {
      savingsElement.textContent = "Saving 15%";
    }

    // Update message
    const messageElement = document.querySelector(".bundle-actions__message");
    if (messageElement) {
      if (productCount < this.minProducts) {
        const remaining = this.minProducts - productCount;
        if (remaining === 1) {
          messageElement.textContent = `Select 1 more product to continue`;
        } else {
          messageElement.textContent = `Select ${remaining} more products to continue`;
        }
      } else {
        messageElement.textContent = "";
      }
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }

  updateAddToCartButton() {
    const selectedCount = this.selectedProducts.filter(
      (product) => product !== null
    ).length;
    const isComplete = selectedCount >= this.minProducts;

    this.addToCartButton.disabled = !isComplete;

    // Update message visibility
    const message = this.addToCartButton.nextElementSibling;
    message.style.display = isComplete ? "none" : "block";
  }

  bindAddToCartButton() {
    this.addToCartButton.addEventListener("click", () =>
      this.addBundleToCart()
    );
  }

  async addBundleToCart() {
    try {
      // Disable the button while processing
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = "Adding to cart...";

      // Filter out null products and ensure minimum selection
      const selectedProducts = this.selectedProducts.filter((p) => p !== null);
      console.log("Selected Products:", selectedProducts); // Debug log 1

      if (selectedProducts.length < this.minProducts) {
        alert(`Please select at least ${this.minProducts} products`);
        return;
      }

      // Get subscription details if present
      const frequencySelect = document.querySelector(
        'select[name="selling_plan"]'
      );
      const frequency = frequencySelect ? frequencySelect.value : null;

      // Wait for ReCharge API
      const rechargeApi = await this.waitForRecharge();
      if (!rechargeApi) {
        throw new Error("ReCharge API not available. Please try again.");
      }

      // Get the bundle handle and product ID from the section settings
      const bundleHandle =
        document.querySelector(".bundle-builder").dataset.bundleHandle;
      const bundleProductId =
        document.querySelector(".bundle-builder").dataset.bundleProductId;

      if (!bundleHandle) {
        throw new Error(
          "Bundle handle is not configured. Please set the bundle handle in the section settings."
        );
      }

      if (!bundleProductId) {
        throw new Error(
          "Bundle product ID is not configured. Please set the bundle product ID in the section settings."
        );
      }

      // Create bundle product data
      const bundleProductData = {
        productId: bundleProductId,
        variantId: bundleProductId, // Using same ID as product since it's a bundle
        handle: bundleHandle,
        ...(frequency && { sellingPlan: frequency }),
      };

      // Create bundle structure
      const bundle = {
        externalVariantId: bundleProductData.variantId,
        externalProductId: bundleProductData.productId,
        selections: selectedProducts.map((product) => {
          const section = document
            .querySelector(`.product-card[data-product-id="${product.id}"]`)
            .closest(".bundle-section");
          console.log("Product Section:", section); // Debug log 2
          console.log("Section Collection ID:", section?.dataset?.collectionId); // Debug log 3

          const bundleItem = {
            collectionId: section?.dataset?.collectionId,
            externalProductId: product.id,
            externalVariantId: product.variantId,
            quantity: 1,
            ...(frequency && { sellingPlan: frequency }),
          };
          console.log("Bundle Item:", bundleItem); // Debug log 4
          return bundleItem;
        }),
      };

      console.log("Full Bundle Structure:", bundle); // Debug log 5

      // Get cart items using ReCharge SDK
      const cartItems = await rechargeApi.bundle.getDynamicBundleItems(
        bundle,
        bundleProductData.handle
      );
      console.log("Raw Cart Items from ReCharge:", cartItems); // Debug log 6

      // Use the cart items directly without modifying their properties
      const enhancedCartItems = cartItems.map((item, index) => {
        const selectedProduct = selectedProducts[index];
        console.log("Mapping Cart Item:", { item, selectedProduct }); // Debug log 7

        return {
          ...item,
          properties: {
            ...item.properties,
            _rc_bundle_quantity: "1",
            _rc_bundle_type: "dynamic",
            _rc_bundle_size: selectedProducts.length.toString(),
          },
        };
      });

      console.log("Enhanced Cart Items:", enhancedCartItems); // Debug log 8

      // Add to cart using Shopify's cart API
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: enhancedCartItems }),
      });

      // Log the response details
      console.log("Shopify response status:", response.status);
      const responseData = await response.json();
      console.log("Shopify response:", responseData);

      if (!response.ok) {
        throw new Error(
          `Failed to add to cart: ${JSON.stringify(responseData)}`
        );
      }

      // Success - track bundle completion with GTM
      console.log("Successfully added to cart!");

      // GTM Tracking: Track bundle completed
      if (window.bundleBuilderGTM) {
        const bundleTotal = selectedProducts.reduce(
          (sum, p) => sum + parseFloat(p.price),
          0
        );
        const bundleItems = selectedProducts.map((product, index) => {
          const card = document.querySelector(
            `[data-product-id="${product.id}"]`
          );
          return {
            item_id: card?.dataset.gtmProductId || product.id,
            item_name: card?.dataset.gtmProductTitle || "Product",
            item_variant: product.optionSummary || "Default",
            price: parseFloat(card?.dataset.gtmProductPrice || product.price),
            quantity: 1,
            index: index,
          };
        });

        window.bundleBuilderGTM.trackBundleCompleted({
          size: selectedProducts.length,
          total: bundleTotal,
          discount: 0, // Calculate discount if applicable
          items: bundleItems,
        });
      }

      this.addToCartButton.textContent = "Added to cart!";
      window.location.href = "/cart";
    } catch (error) {
      console.error("Error adding bundle to cart:", error);
      alert("There was an error adding your bundle to cart. Please try again.");

      // Re-enable the button
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent = "Add Bundle to Cart";
    }
  }

  setupImageCarousel(imageUrls, productTitle) {
    const mediaList = document.querySelector(
      ".product__media-list .slider--mobile"
    );
    const thumbnailList = document.querySelector(".thumbnail-list");

    // Clear existing content
    mediaList.innerHTML = "";
    thumbnailList.innerHTML = "";

    // If there's only one image, show it directly without carousel
    if (imageUrls.length === 1) {
      const li = document.createElement("li");
      li.className = "product__media-item";
      li.innerHTML = `
        <div class="product__media" style="padding-top: 100.0%;">
          <img src="${imageUrls[0]}" alt="${
        productTitle || ""
      }" loading="lazy" width="400" height="400">
        </div>
      `;
      mediaList.appendChild(li);

      // Hide thumbnails for single image
      const thumbnailsContainer = document.querySelector(
        ".product__thumbnails"
      );
      thumbnailsContainer.style.display = "none";

      return;
    }

    // For multiple images, create carousel
    imageUrls.forEach((imageUrl, index) => {
      // Add main image slide
      const li = document.createElement("li");
      li.className = "product__media-item";
      li.innerHTML = `
        <div class="product__media" style="padding-top: 100.0%;">
          <img src="${imageUrl}" alt="${
        productTitle || ""
      }" loading="lazy" width="400" height="400">
        </div>
      `;
      mediaList.appendChild(li);

      // Add thumbnail
      const thumbLi = document.createElement("li");
      thumbLi.className = `thumbnail-item ${index === 0 ? "active" : ""}`;
      thumbLi.innerHTML = `<img src="${imageUrl}" alt="Thumbnail ${
        index + 1
      }" loading="lazy">`;
      thumbLi.addEventListener("click", () => this.goToSlide(index));
      thumbnailList.appendChild(thumbLi);
    });

    this.currentSlide = 0;
    this.totalSlides = imageUrls.length;

    // Show thumbnails for multiple images
    const thumbnailsContainer = document.querySelector(".product__thumbnails");
    thumbnailsContainer.style.display = "block";
  }

  goToSlide(index) {
    const slider = document.querySelector(".slider--mobile");
    const thumbnails = document.querySelectorAll(".thumbnail-item");

    this.currentSlide = index;
    slider.style.transform = `translateX(-${index * 100}%)`;

    // Update thumbnail active state
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle("active", i === index);
    });
  }

  initMaxAlert() {
    const container = document.querySelector(".bundle-max-alert-container");
    const alert = container.querySelector(".bundle-max-alert");
    const closeButton = alert.querySelector(".bundle-max-alert__button");

    closeButton.addEventListener("click", () => this.hideMaxAlert());
    container.addEventListener("click", (e) => {
      if (e.target === container) {
        this.hideMaxAlert();
      }
    });
  }

  showMaxAlert() {
    const container = document.querySelector(".bundle-max-alert-container");
    const alert = container.querySelector(".bundle-max-alert");

    // Force a reflow before adding the active class
    container.offsetHeight;
    alert.offsetHeight;

    container.classList.add("active");
    alert.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  hideMaxAlert() {
    const container = document.querySelector(".bundle-max-alert-container");
    const alert = container.querySelector(".bundle-max-alert");

    container.classList.remove("active");
    alert.classList.remove("active");

    // Wait for animation to complete before re-enabling scroll
    setTimeout(() => {
      document.body.style.overflow = "";
    }, 300);
  }

  bindFeaturedProducts() {
    const featuredProductCards = document.querySelectorAll(
      ".featured-products .card-wrapper"
    );

    featuredProductCards.forEach((card) => {
      const addToCartBtn = card.querySelector("add-to-cart");

      if (addToCartBtn) {
        addToCartBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addFeaturedProductToCart(card, addToCartBtn);
        });
      }
    });
  }

  async addFeaturedProductToCart(card, button) {
    try {
      // Get product data from the card
      const productId = card.dataset.productId;
      const variantId = card.dataset.variantId;

      if (!productId || !variantId) {
        throw new Error("Product ID or Variant ID not found");
      }

      // Add to cart using Shopify's cart API
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(variantId),
              quantity: 1,
            },
          ],
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          `Failed to add to cart: ${JSON.stringify(responseData)}`
        );
      }

      // Success - the theme's product card will handle the UI feedback
      console.log("Featured product added to cart successfully");
    } catch (error) {
      console.error("Error adding featured product to cart:", error);

      // Show error message to user
      alert(
        "There was an error adding this product to your cart. Please try again."
      );
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new BundleBuilder();
});

function getColumnsCount() {
  if (window.innerWidth <= 749) return 2;
  if (window.innerWidth <= 989) return 2;
  return 4;
}

function updateProductVisibility() {
  document.querySelectorAll(".bundle-section__products").forEach((section) => {
    const sectionIndex = section.getAttribute("data-section-products");
    const cards = section.querySelectorAll(".product-card");
    const loadMoreBtn = document.querySelector(
      '.load-more-btn[data-section-products="' + sectionIndex + '"]'
    );
    const initialVisible = 4; // Always show 4 products initially
    const allVisible =
      section.getAttribute("data-all-visible") === "true" ||
      (loadMoreBtn && loadMoreBtn.getAttribute("data-all-visible") === "true");

    if (allVisible) {
      cards.forEach((card) => {
        card.classList.remove("is-hidden");
        card.classList.remove("product-card--hidden");
      });
      if (loadMoreBtn && loadMoreBtn.parentNode)
        loadMoreBtn.parentNode.removeChild(loadMoreBtn);
      return;
    }

    cards.forEach((card, idx) => {
      if (idx < initialVisible) {
        card.classList.remove("is-hidden");
        card.classList.remove("product-card--hidden");
      } else {
        card.classList.add("is-hidden");
        card.classList.add("product-card--hidden");
      }
    });

    if (cards.length > initialVisible) {
      if (loadMoreBtn) loadMoreBtn.classList.remove("is-hidden");
    } else {
      if (loadMoreBtn && loadMoreBtn.parentNode)
        loadMoreBtn.parentNode.removeChild(loadMoreBtn);
    }
  });
}

function bindLoadMoreButtons() {
  document.querySelectorAll(".load-more-btn").forEach((btn) => {
    btn.addEventListener("click", function () {
      const sectionIndex = btn.getAttribute("data-section-products");
      const section = document.querySelector(
        '.bundle-section__products[data-section-products="' +
          sectionIndex +
          '"]'
      );
      section.querySelectorAll(".product-card").forEach((card) => {
        card.classList.remove("is-hidden");
        card.classList.remove("product-card--hidden");
      });
      section.setAttribute("data-all-visible", "true");
      btn.setAttribute("data-all-visible", "true");
      if (btn.parentNode) btn.parentNode.removeChild(btn);
    });
  });
}

document.addEventListener("DOMContentLoaded", function () {
  updateProductVisibility();
  bindLoadMoreButtons();
  window.addEventListener("resize", updateProductVisibility);
});
